import React, { useEffect, useState } from "react";

import { FormSection } from "../../../../components/layout/forms/FormSection";

import TextField from "../../../../components/input/textfield/TextField";
import TextArea from "../../../../components/input/textarea/TextArea";
import StatusAlert from "../../../../components/alerts/StatusAlert";
import Select from "../../../../components/input/select/Select";

import { useCreatorGroups } from "../hooks/useCreatorGroups";

import "./GroupCreatorEditForm.css";

const VISIBILITY_OPTIONS = [
  { label: "Public", value: "public" },
  { label: "Private", value: "private" },
];

const initialFormData = (group) => ({
  groupName: group?.groupName || "",
  groupCode: group?.groupCode || "",
  groupPolicy: group?.groupPolicy || "",
  visibility: group?.visibility || "private",
});

const GroupCreatorEditForm = ({ username, group, onClose, onSuccess }) => {
  const { updateCreatorGroup } = useCreatorGroups(username);

  const [formData, setFormData] = useState(() => initialFormData(group));

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const groupTag = String(group?.groupTag || "");

  useEffect(() => {
    setFormData(initialFormData(group));
    setFormError("");
  }, [group]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));

    setFormError("");
  };

  const handleClose = () => {
    if (submitting) return;

    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submitting) return;

    if (!username || !groupTag) {
      setFormError("Creator or group information is missing.");
      return;
    }

    const groupName = formData.groupName.trim();
    const groupPolicy = formData.groupPolicy.trim();
    const visibility = formData.visibility;

    if (!groupName) {
      setFormError("Group name is required.");
      return;
    }

    if (!groupPolicy) {
      setFormError("Group privacy and rules are required.");
      return;
    }

    if (!VISIBILITY_OPTIONS.some((item) => item.value === visibility)) {
      setFormError("Please select a valid visibility option.");
      return;
    }

    setSubmitting(true);
    setFormError("");

    try {
      const result = await updateCreatorGroup({
        groupTag,
        groupName,
        groupCode: formData.groupCode,
        groupPolicy,
        visibility,
      });

      if (!result || result.success === false) {
        setFormError(result?.message || "Failed to update group.");
        return;
      }

      await onSuccess?.(result);

      onClose?.();
    } catch (error) {
      setFormError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update group.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isSubmitDisabled =
    submitting ||
    !username ||
    !groupTag ||
    !formData.groupName.trim() ||
    !formData.groupPolicy.trim() ||
    !formData.visibility;

  return (
    <form
      className="creator-edit-group-form"
      onSubmit={handleSubmit}
      aria-busy={submitting}
    >
      {formError && (
        <div className="creator-edit-group-form__alert">
          <StatusAlert
            status={{
              type: "error",
              message: formError,
            }}
          />
        </div>
      )}

      <FormSection className="creator-edit-group-form__fields">
        <TextField
          label="Group Name"
          name="groupName"
          value={formData.groupName}
          onChange={handleChange}
          placeholder="e.g. European Tour"
          maxLength={100}
          required
          disabled={submitting}
        />

        <TextField
          label="Group Code"
          name="groupCode"
          value={formData.groupCode}
          placeholder="Group access code"
          disabled
        />

        <TextArea
          label="Group Privacy & Rules"
          name="groupPolicy"
          value={formData.groupPolicy}
          onChange={handleChange}
          placeholder="Explain the group rules and how members should use its content."
          maxLength={2000}
          maxHeight={140}
          required
          disabled={submitting}
        />

        <Select
          label="Visibility"
          name="visibility"
          value={formData.visibility}
          onChange={handleChange}
          items={VISIBILITY_OPTIONS}
          required
          disabled={submitting}
        />
      </FormSection>

      <div className="creator-edit-group-form__actions">
        <button
          type="button"
          className="creator-edit-group-form__cancel"
          onClick={handleClose}
          disabled={submitting}
        >
          Cancel
        </button>

        <button
          type="submit"
          className="creator-edit-group-form__submit"
          disabled={isSubmitDisabled}
        >
          {submitting ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
};

export default GroupCreatorEditForm;
